import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import {
  isCancellationStatus,
  isNegativeStatus,
} from 'app/components/Placement/NextPlacementStatus/StatusDefaults';
import { selectUser } from 'oidc/user.selectors';
import { useReadOnly } from 'oidc/userRoles';
import { selectPlacementDetails } from 'store/redux-store/placement-details/selectors';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import {
  candidateEventSubmissionControlActions,
  candidateEventSubmissionControlReducer,
  candidateEventSubmissionControlSliceKey,
} from './store/candidateEventSubmissionControl.redux';
import { candidateEventSubmissionControlSaga } from './store/candidateEventSubmissionControl.saga';
import {
  selectPlacementSubmissionControlForPlacement,
  selectPlacementSubmissionControlLoading,
  selectCandidateEventSubmissionControlSavingAffiliationIds,
} from './store/candidateEventSubmissionControl.selector';

export const usePlacementSubmissionControl = (placementId: number) => {
  useInjectReducer({ key: candidateEventSubmissionControlSliceKey, reducer: candidateEventSubmissionControlReducer });
  useInjectSaga({ key: candidateEventSubmissionControlSliceKey, saga: candidateEventSubmissionControlSaga });

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const control = useSelector(state => selectPlacementSubmissionControlForPlacement(state, placementId));
  const loading = useSelector(selectPlacementSubmissionControlLoading);
  const savingAffiliationIds = useSelector(selectCandidateEventSubmissionControlSavingAffiliationIds);

  useEffect(() => {
    if (placementId > 0) {
      dispatch(candidateEventSubmissionControlActions.fetchSubmissionControlByPlacement({ placementId }));
    }
  }, [dispatch, placementId]);

  const updateSubmissionPause = (isSubmissionPaused: boolean) => {
    if (!control?.travelerId || !control?.affiliationId) {
      return;
    }
    dispatch(
      candidateEventSubmissionControlActions.updateSubmissionPause({
        travelerId: control.travelerId,
        affiliationId: control.affiliationId,
        isSubmissionPaused,
      }),
    );
  };

  return {
    control,
    loading,
    savingAffiliationIds,
    updateSubmissionPause,
    userId: user?.userInfo?.employeeId,
  };
};

export const useSubmissionPausePlacementToggleDisabled = () => {
  const { currentPlacementStatus } = useSelector(selectPlacementStatus);
  const { header } = useSelector(selectPlacementDetails);
  const { readOnly } = useReadOnly();

  return (
    readOnly ||
    !!header?.isArchived ||
    isNegativeStatus(currentPlacementStatus) ||
    isCancellationStatus(currentPlacementStatus)
  );
};
